import React, { useEffect, useState, useRef } from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box,
  IconButton,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  Menu,
  MenuItem,
  ListItemText,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  InputAdornment,
  Grid,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  Label as LabelIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  kanbanService,
  KanbanBoard,
  KanbanList as KanbanListType,
  KanbanCard as KanbanCardType,
} from '../../services/kanbanService';
import { useKanbanStore } from '../../store/kanbanStore';
import KanbanList from '../../components/Kanban/KanbanList';
import axios from 'axios';
import { API_CONFIG } from '../../config/api';

interface KanbanListProps {
  list: any;
  cards: any[];
  totalCards?: number;
  hasMoreCards?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  visibleCount?: number;
  prevList?: any | null;
  nextList?: any | null;
  onMoveCard?: (cardId: string, targetListId: string) => void;
  onMoveCardInList?: (cardId: string, targetPosition: number) => void;
}

const SortableList: React.FC<KanbanListProps> = ({ 
  list, 
  cards, 
  totalCards = cards.length,
  hasMoreCards = false,
  isLoadingMore = false,
  onLoadMore,
  visibleCount = cards.length,
  prevList = null,
  nextList = null,
  onMoveCard,
  onMoveCardInList,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: list.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <KanbanList 
        list={list} 
        cards={cards}
        totalCards={totalCards}
        hasMoreCards={hasMoreCards}
        isLoadingMore={isLoadingMore}
        onLoadMore={onLoadMore}
        visibleCount={visibleCount}
        prevList={prevList}
        nextList={nextList}
        onMoveCard={onMoveCard}
        onMoveCardInList={onMoveCardInList}
      />
    </div>
  );
};

const BoardView: React.FC = () => {
  const { boardId } = useParams<{ boardId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { currentBoard, setCurrentBoard, moveCard, reorderLists, reorderCardInList } =
    useKanbanStore();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [openListDialog, setOpenListDialog] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [selectedLabelId, setSelectedLabelId] = useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [clientMenuAnchor, setClientMenuAnchor] = useState<null | HTMLElement>(null);
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [boardMenuAnchor, setBoardMenuAnchor] = useState<null | HTMLElement>(null);
  const [labelsDialogOpen, setLabelsDialogOpen] = useState(false);
  const [editingLabelId, setEditingLabelId] = useState<string | null>(null);
  const [editingLabelName, setEditingLabelName] = useState('');
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState('#61BD4F');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const topScrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Estados para paginação visual - mostrar menos cards para melhor performance
  const [cardsPerList, setCardsPerList] = useState<{ [listId: string]: number }>({});
  const [isLoadingMore, setIsLoadingMore] = useState<{ [listId: string]: boolean }>({});
  const INITIAL_CARDS_PER_LIST = 8; // Mostrar apenas 8 cards inicialmente
  const CARDS_TO_LOAD_MORE = 15; // Carregar mais 15 cards por vez
  
  const isDark = theme.palette.mode === 'dark';

  // Função para carregar mais cards em uma lista específica
  const loadMoreCards = (listId: string) => {
    setIsLoadingMore(prev => ({ ...prev, [listId]: true }));
    
    // Simular delay para UX
    setTimeout(() => {
      setCardsPerList(prev => ({
        ...prev,
        [listId]: (prev[listId] || INITIAL_CARDS_PER_LIST) + CARDS_TO_LOAD_MORE
      }));
      setIsLoadingMore(prev => ({ ...prev, [listId]: false }));
    }, 300);
  };

  // Função para obter cards visíveis de uma lista
  const getVisibleCards = (cards: any[], listId: string) => {
    const limit = cardsPerList[listId] || INITIAL_CARDS_PER_LIST;
    return cards.slice(0, limit);
  };

  // Resetar paginação quando filtros mudarem
  React.useEffect(() => {
    setCardsPerList({});
    setIsLoadingMore({});
  }, [selectedLabelId, selectedClientId, boardId]);

  // Buscar clientes para o filtro
  const { data: clientsData } = useQuery({
    queryKey: ['clients', clientSearchQuery],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: '1',
        limit: '100',
        ...(clientSearchQuery && { search: clientSearchQuery }),
      });
      const response = await axios.get(`${API_CONFIG.BASE_URL}/clients?${params}`);
      return response.data.success ? response.data.data : [];
    },
    enabled: Boolean(clientMenuAnchor),
  });

  // Ordenar clientes alfabeticamente
  const sortedClients = React.useMemo(() => {
    if (!clientsData) return [];
    return [...clientsData].sort((a, b) => 
      (a.company_name || '').localeCompare(b.company_name || '', 'pt-BR')
    );
  }, [clientsData]);

  // Sincronizar scroll horizontal entre topo e conteúdo
  const handleMainScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (topScrollContainerRef.current && e.currentTarget) {
      topScrollContainerRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  const handleTopScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (scrollContainerRef.current && e.currentTarget) {
      scrollContainerRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Buscar board
  const { data: board, isLoading, error } = useQuery({
    queryKey: ['kanban-board', boardId],
    queryFn: () => kanbanService.getBoard(boardId!),
    enabled: !!boardId,
  });

  // Sincronizar board com store quando dados mudarem
  useEffect(() => {
    if (board) {
      setCurrentBoard(board);
    }
  }, [board, setCurrentBoard]);

  // Criar lista
  const createListMutation = useMutation({
    mutationFn: (name: string) =>
      kanbanService.createList({
        board_id: boardId!,
        name,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-board', boardId] });
      setOpenListDialog(false);
      setNewListName('');
    },
  });

  // Atualizar label
  const updateLabelMutation = useMutation({
    mutationFn: ({ labelId, data }: { labelId: string; data: { name?: string; color?: string } }) =>
      kanbanService.updateLabel(labelId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-board', boardId] });
      queryClient.invalidateQueries({ queryKey: ['kanban-labels', boardId] });
      setEditingLabelId(null);
      setEditingLabelName('');
    },
  });

  // Deletar label
  const deleteLabelMutation = useMutation({
    mutationFn: (labelId: string) => kanbanService.deleteLabel(labelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-board', boardId] });
      queryClient.invalidateQueries({ queryKey: ['kanban-labels', boardId] });
    },
  });

  // Criar label
  const createLabelMutation = useMutation({
    mutationFn: (data: { name: string; color: string }) =>
      kanbanService.createLabel(boardId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-board', boardId] });
      queryClient.invalidateQueries({ queryKey: ['kanban-labels', boardId] });
      setNewLabelName('');
      setNewLabelColor('#61BD4F');
    },
  });

  // Mover cartão
  const moveCardMutation = useMutation({
    mutationFn: (data: {
      card_id: string;
      source_list_id: string;
      destination_list_id: string;
      destination_position: number;
    }) => kanbanService.moveCard(data),
    onMutate: async (variables) => {
      // Cancelar queries em andamento
      await queryClient.cancelQueries({ queryKey: ['kanban-board', boardId] });

      // Snapshot do estado anterior
      const previousBoard = queryClient.getQueryData(['kanban-board', boardId]);

      // Atualização otimista
      const lists = (currentBoard?.lists || []).map((list: KanbanListType) => {
        if (list.id === variables.source_list_id) {
          return {
            ...list,
            cards: (list.cards || []).filter((c: KanbanCardType) => c.id !== variables.card_id),
          };
        }
        if (list.id === variables.destination_list_id) {
          const card = (currentBoard?.lists || [])
            .find((l: KanbanListType) => l.id === variables.source_list_id)
            ?.cards?.find((c: KanbanCardType) => c.id === variables.card_id);
          if (card) {
            const newCards = [...(list.cards || [])];
            newCards.splice(variables.destination_position, 0, {
              ...card,
              list_id: variables.destination_list_id,
            });
            return {
              ...list,
              cards: newCards.map((c: KanbanCardType, i: number) => ({ ...c, position: i })),
            };
          }
        }
        return list;
      });

      queryClient.setQueryData(['kanban-board', boardId], {
        ...currentBoard,
        lists,
      });

      return { previousBoard };
    },
    onError: (err, variables, context) => {
      // Rollback em caso de erro
      if (context?.previousBoard) {
        queryClient.setQueryData(['kanban-board', boardId], context.previousBoard);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-board', boardId] });
    },
  });

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    // Verificar se é uma lista sendo arrastada
    if (!currentBoard) return;
    
    const sourceList = currentBoard.lists?.find((l: KanbanListType) => l.id === activeId);
    if (sourceList) {
      const destinationList = currentBoard.lists?.find((l: KanbanListType) => l.id === overId);
      if (destinationList && sourceList.id !== destinationList.id && currentBoard.lists) {
        const oldIndex = currentBoard.lists.findIndex((l: KanbanListType) => l.id === activeId);
        const newIndex = currentBoard.lists.findIndex((l: KanbanListType) => l.id === overId);
        reorderLists(oldIndex, newIndex);
        // TODO: Chamar API para reordenar listas
        return;
      }
      return;
    }

    // É um cartão sendo arrastado
    if (!board || !board.lists) return;
    
    // Encontrar a lista que contém o cartão sendo arrastado (usar board.lists não filtrado)
    const sourceCardList = board.lists.find((list: KanbanListType) =>
      list.cards?.some((card: KanbanCardType) => card.id === activeId)
    );

    if (!sourceCardList) return;

    const sourceCard = sourceCardList.cards?.find((c: KanbanCardType) => c.id === activeId);
    if (!sourceCard) return;

    // Encontrar a lista de destino (usar board.lists não filtrado)
    // Primeiro, verificar se overId é o ID de uma lista (droppable)
    let destinationList = board.lists.find((list: KanbanListType) => list.id === overId);
    
    // Se não for uma lista, verificar se é um cartão dentro de uma lista
    if (!destinationList) {
      destinationList = board.lists.find((list: KanbanListType) =>
        list.cards?.some((card: KanbanCardType) => card.id === overId)
      );
    }

    if (!destinationList) return;

    // Se está movendo para a mesma lista, calcular nova posição
    if (sourceCardList.id === destinationList.id) {
      const sourceIndex = sourceCardList.cards!.findIndex((c: KanbanCardType) => c.id === activeId);
      let destinationIndex = sourceIndex;

      // Se está sobre outro cartão, calcular a posição
      if (destinationList.cards?.some((c: KanbanCardType) => c.id === overId)) {
        destinationIndex = destinationList.cards.findIndex((c: KanbanCardType) => c.id === overId);
      }

      // Se não mudou de posição, não fazer nada
      if (sourceIndex === destinationIndex) return;

      // Reordenar dentro da mesma lista
      reorderCardInList(sourceCardList.id, sourceIndex, destinationIndex);
      // TODO: Chamar API para reordenar cartão na mesma lista
      return;
    }

    // Movendo para outra lista
    const sourceIndex = sourceCardList.cards!.findIndex((c: KanbanCardType) => c.id === activeId);
    let destinationIndex = destinationList.cards?.length || 0;

    // Se está sobre um cartão na lista de destino, calcular a posição
    if (destinationList.cards?.some((c: KanbanCardType) => c.id === overId)) {
      destinationIndex = destinationList.cards.findIndex((c: KanbanCardType) => c.id === overId);
    }

    // Atualização otimista
    moveCard(activeId, sourceCardList.id, destinationList.id, destinationIndex);

    // Chamar API
    moveCardMutation.mutate({
      card_id: activeId,
      source_list_id: sourceCardList.id,
      destination_list_id: destinationList.id,
      destination_position: destinationIndex,
    });
  };

  const handleCreateList = () => {
    if (!newListName.trim()) return;
    createListMutation.mutate(newListName);
  };

  // Filtrar cards por label se selecionado (deve estar antes dos returns condicionais)
  const filteredLists = React.useMemo(() => {
    if (!board || !board.lists) return [];
    
    return board.lists.map((list: KanbanListType) => {
      let filteredCards = list.cards || [];
      
      // Filtrar por label
      if (selectedLabelId) {
        filteredCards = filteredCards.filter((card: KanbanCardType) =>
          card.labels?.some((label: any) => label.id === selectedLabelId)
        );
      }
      
      // Filtrar por cliente
      if (selectedClientId) {
        filteredCards = filteredCards.filter((card: KanbanCardType) =>
          card.client_id === selectedClientId
        );
      }
      
      return {
        ...list,
        cards: filteredCards,
      };
    });
  }, [board?.lists, selectedLabelId, selectedClientId]);

  // Coletar todas as labels únicas do board (deve estar antes dos returns condicionais)
  const allLabels = React.useMemo(() => {
    if (!board) return [];
    
    // Primeiro, usar labels do board se disponíveis
    if (board.labels && board.labels.length > 0) {
      return board.labels;
    }
    
    // Se não houver labels no board, coletar dos cards
    if (!board.lists) return [];
    const labelsMap = new Map();
    board.lists.forEach((list: KanbanListType) => {
      (list.cards || []).forEach((card: KanbanCardType) => {
        (card.labels || []).forEach((label: any) => {
          if (label && typeof label === 'object' && label.id) {
            if (!labelsMap.has(label.id)) {
              labelsMap.set(label.id, label);
            }
          }
        });
      });
    });
    return Array.from(labelsMap.values());
  }, [board]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" bgcolor="background.default">
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  if (error || !board) {
    return (
      <Box p={3} bgcolor="background.default">
        <Alert severity="error">Erro ao carregar o quadro. Tente novamente.</Alert>
      </Box>
    );
  }

  const lists = filteredLists;
  const activeCard = activeId && board
    ? (board.lists || [])
        .flatMap((list: KanbanListType) => list.cards || [])
        .find((card: KanbanCardType) => card.id === activeId)
    : null;

  return (
    <Box
      sx={{
        height: { xs: '100dvh', md: '100vh' },
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box sx={{
        p: { xs: 1.5, md: 2 },
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
        gap: { xs: 1.5, md: 2 },
        flexWrap: 'wrap',
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[1],
        flexShrink: 0,
      }}>
        {/* Linha 1: Voltar + Título + Menu */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: '1 1 auto' }}>
          <IconButton 
            onClick={() => navigate('/kanban')}
            size={isMobile ? 'medium' : 'small'}
            sx={{ color: theme.palette.primary.main, flexShrink: 0, '&:hover': { backgroundColor: theme.palette.action.hover } }}
            aria-label="Voltar"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography 
            variant="h5" 
            component="h1" 
            noWrap
            sx={{ 
              color: theme.palette.text.primary,
              fontWeight: 'bold',
              fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {board.name}
          </Typography>
          <IconButton 
            size="small"
            sx={{ color: theme.palette.primary.main, flexShrink: 0, '&:hover': { backgroundColor: theme.palette.action.hover } }}
            onClick={(e) => setBoardMenuAnchor(e.currentTarget)}
            aria-label="Menu do quadro"
          >
            <MoreVertIcon />
          </IconButton>
        </Box>
        
        {/* Linha 2 (mobile) / Centro (desktop): Filtros */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          flexWrap: 'nowrap', 
          overflowX: 'auto',
          overflowY: 'hidden',
          flex: { md: 1 },
          justifyContent: { md: 'center' },
          minHeight: 40,
          pb: { xs: 0.5, md: 0 },
          '&::-webkit-scrollbar': { height: 4 },
        }}>
          <FilterListIcon sx={{ color: theme.palette.text.secondary, fontSize: 20, flexShrink: 0 }} />
          
          {/* Filtro "Todos" */}
          <Chip
            label="Todos"
            onClick={() => {
              setSelectedLabelId(null);
              setSelectedClientId(null);
            }}
            size="small"
            sx={{ 
              cursor: 'pointer',
              backgroundColor: selectedLabelId === null && selectedClientId === null 
                ? theme.palette.primary.main + '20' 
                : 'transparent',
              color: selectedLabelId === null && selectedClientId === null 
                ? theme.palette.primary.main 
                : theme.palette.text.secondary,
              border: `1px solid ${selectedLabelId === null && selectedClientId === null 
                ? theme.palette.primary.main + '50' 
                : theme.palette.divider}`,
              '&:hover': {
                backgroundColor: theme.palette.primary.main + '15',
                borderColor: theme.palette.primary.main + '60',
              },
            }}
          />
          
          {/* Filtro de Labels */}
          {allLabels.length > 0 && allLabels.map((label: any) => (
            <Chip
              key={label.id}
              label={label.name}
              onClick={() => setSelectedLabelId(label.id === selectedLabelId ? null : label.id)}
              size="small"
              sx={{
                cursor: 'pointer',
                backgroundColor: label.id === selectedLabelId ? label.color : 'transparent',
                borderColor: label.color,
                borderWidth: 1,
                borderStyle: 'solid',
                color: label.id === selectedLabelId ? '#fff' : label.color,
                '&:hover': {
                  backgroundColor: label.color,
                  color: '#fff',
                },
              }}
            />
          ))}
          
          {/* Filtro de Cliente */}
          <Chip
            icon={<BusinessIcon sx={{ color: selectedClientId ? theme.palette.primary.main : theme.palette.text.secondary }} />}
            label={selectedClientId 
              ? (sortedClients?.find(c => c.id === selectedClientId)?.company_name || 'Cliente')
              : 'Filtrar por Cliente'}
            onClick={(e) => setClientMenuAnchor(e.currentTarget)}
            size="small"
            sx={{ 
              cursor: 'pointer',
              backgroundColor: selectedClientId 
                ? theme.palette.primary.main + '20' 
                : 'transparent',
              color: selectedClientId 
                ? theme.palette.primary.main 
                : theme.palette.text.secondary,
              border: `1px solid ${selectedClientId 
                ? theme.palette.primary.main + '50' 
                : theme.palette.divider}`,
              '&:hover': {
                backgroundColor: theme.palette.primary.main + '15',
                borderColor: theme.palette.primary.main + '60',
              },
            }}
            onDelete={selectedClientId ? () => setSelectedClientId(null) : undefined}
          />
        </Box>
        
        {/* Menu de Seleção de Cliente */}
        <Menu
          anchorEl={clientMenuAnchor}
          open={Boolean(clientMenuAnchor)}
          onClose={() => {
            setClientMenuAnchor(null);
            setClientSearchQuery('');
          }}
          PaperProps={{
            sx: {
              maxHeight: 400,
              width: isMobile ? 'calc(100vw - 32px)' : 300,
              maxWidth: isMobile ? 'none' : 300,
              background: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '8px',
              mt: 1,
            },
          }}
        >
          <Box sx={{ p: 1 }}>
            <TextField
              placeholder="Pesquisar cliente..."
              value={clientSearchQuery}
              onChange={(e) => setClientSearchQuery(e.target.value)}
              size="small"
              fullWidth
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: theme.palette.divider,
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main + '80',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
            />
          </Box>
          <Divider />
          <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
            {sortedClients && sortedClients.length > 0 ? (
              sortedClients.map((client: any) => (
                <MenuItem
                  key={client.id}
                  selected={selectedClientId === client.id}
                  onClick={() => {
                    setSelectedClientId(client.id === selectedClientId ? null : client.id);
                    setClientMenuAnchor(null);
                    setClientSearchQuery('');
                  }}
                  sx={{
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.main + '20',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.main + '30',
                      },
                    },
                  }}
                >
                  <ListItemText
                    primary={client.company_name}
                    secondary={client.contact_name}
                  />
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                <ListItemText primary="Nenhum cliente encontrado" />
              </MenuItem>
            )}
          </Box>
        </Menu>
        
        {/* Menu do Board */}
        <Menu
          anchorEl={boardMenuAnchor}
          PaperProps={{
            sx: {
              background: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '8px',
              mt: 1,
            }
          }}
          open={Boolean(boardMenuAnchor)}
          onClose={() => setBoardMenuAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              setLabelsDialogOpen(true);
              setBoardMenuAnchor(null);
            }}
            sx={{
              color: theme.palette.text.primary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <ListItemIcon>
              <LabelIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
            </ListItemIcon>
            <ListItemText primary="Gerenciar Labels" primaryTypographyProps={{ sx: { color: theme.palette.text.primary } }} />
          </MenuItem>
        </Menu>
      </Box>
      
      {/* Dialog de Gerenciamento de Labels */}
      <Dialog
        open={labelsDialogOpen}
        onClose={() => {
          setLabelsDialogOpen(false);
          setEditingLabelId(null);
          setEditingLabelName('');
          setNewLabelName('');
          setNewLabelColor('#61BD4F');
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LabelIcon />
            <Typography variant="h6">Gerenciar Labels</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Formulário para criar nova label */}
          <Box sx={{ mb: 3, p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: theme.palette.text.primary }}>
              Criar Nova Label
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nome da Label"
                  value={newLabelName}
                  onChange={(e) => setNewLabelName(e.target.value)}
                  size="small"
                  fullWidth
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newLabelName.trim()) {
                      createLabelMutation.mutate({
                        name: newLabelName.trim(),
                        color: newLabelColor,
                      });
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    type="color"
                    value={newLabelColor}
                    onChange={(e) => setNewLabelColor(e.target.value)}
                    size="small"
                    sx={{
                      width: 60,
                      height: 40,
                      '& input': {
                        cursor: 'pointer',
                        padding: 0,
                        border: 'none',
                      },
                    }}
                  />
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      backgroundColor: newLabelColor,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    if (newLabelName.trim()) {
                      createLabelMutation.mutate({
                        name: newLabelName.trim(),
                        color: newLabelColor,
                      });
                    }
                  }}
                  disabled={!newLabelName.trim() || createLabelMutation.isPending}
                  startIcon={<AddIcon />}
                >
                  {createLabelMutation.isPending ? 'Criando...' : 'Criar'}
                </Button>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Lista de labels existentes */}
          {allLabels.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              Nenhuma label criada ainda.
            </Typography>
          ) : (
            <List>
              {allLabels.map((label: any) => (
                <ListItem
                  key={label.id}
                  sx={{
                    border: `1px solid ${label.color}20`,
                    borderRadius: 1,
                    mb: 1,
                    backgroundColor: editingLabelId === label.id ? 'action.hover' : 'transparent',
                  }}
                >
                  <ListItemIcon>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '4px',
                        backgroundColor: label.color,
                      }}
                    />
                  </ListItemIcon>
                  {editingLabelId === label.id ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <TextField
                        value={editingLabelName}
                        onChange={(e) => setEditingLabelName(e.target.value)}
                        size="small"
                        fullWidth
                        autoFocus
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            if (editingLabelName.trim()) {
                              updateLabelMutation.mutate({
                                labelId: label.id,
                                data: { name: editingLabelName.trim() },
                              });
                            }
                          } else if (e.key === 'Escape') {
                            setEditingLabelId(null);
                            setEditingLabelName('');
                          }
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (editingLabelName.trim()) {
                            updateLabelMutation.mutate({
                              labelId: label.id,
                              data: { name: editingLabelName.trim() },
                            });
                          }
                        }}
                        disabled={!editingLabelName.trim() || updateLabelMutation.isPending}
                      >
                        <CheckIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingLabelId(null);
                          setEditingLabelName('');
                        }}
                      >
                        <CancelIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    <>
                      <ListItemText
                        primary={label.name}
                        sx={{ flex: 1 }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => {
                            setEditingLabelId(label.id);
                            setEditingLabelName(label.name);
                          }}
                          sx={{ mr: 1 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => {
                            if (window.confirm(`Tem certeza que deseja deletar a label "${label.name}"?`)) {
                              deleteLabelMutation.mutate(label.id);
                            }
                          }}
                          disabled={deleteLabelMutation.isPending}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </>
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setLabelsDialogOpen(false);
            setEditingLabelId(null);
            setEditingLabelName('');
            setNewLabelName('');
            setNewLabelColor('#61BD4F');
          }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Board Content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Barra de scroll horizontal superior - Títulos das listas */}
        <Box
          ref={topScrollContainerRef}
          onScroll={handleTopScroll}
          sx={{
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            px: { xs: 1, md: 2 },
            pt: { xs: 1.5, md: 2 },
            pb: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            background: theme.palette.background.paper,
            // Customização da barra de scroll horizontal
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: 'action.hover',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'divider',
              borderRadius: '10px',
              border: `1px solid ${theme.palette.divider}`,
              '&:hover': { bgcolor: 'primary.main' },
            },
            '&::-webkit-scrollbar-thumb:active': { bgcolor: 'primary.dark' },
            scrollbarWidth: 'thin',
            scrollbarColor: `${theme.palette.divider} ${theme.palette.action.hover}`,
          }}
        >
          <Box display="flex" gap={isMobile ? 1.5 : 2} sx={{ minWidth: 'max-content' }}>
            {lists.map((list: KanbanListType) => (
              <Box
                key={list.id}
                sx={{
                  minWidth: isMobile ? 260 : 272,
                  maxWidth: isMobile ? 260 : 272,
                  p: 1,
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {list.name}
                </Typography>
              </Box>
            ))}
            <Box sx={{ minWidth: isMobile ? 260 : 272, maxWidth: isMobile ? 260 : 272, p: 1 }} />
          </Box>
        </Box>

        {/* Área de conteúdo das listas */}
        <Box
          ref={scrollContainerRef}
          onScroll={handleMainScroll}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            p: { xs: 1, sm: 1.5, md: 2 },
            display: 'flex',
            alignItems: 'flex-start',
            bgcolor: theme.palette.mode === 'dark'
              ? alpha(theme.palette.primary.main, 0.03)
              : alpha(theme.palette.primary.main, 0.02),
            // Customização da barra de scroll horizontal
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: 'action.hover',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'divider',
            borderRadius: '10px',
            border: `1px solid ${theme.palette.divider}`,
            '&:hover': { bgcolor: 'primary.main' },
          },
          '&::-webkit-scrollbar-thumb:active': { bgcolor: 'primary.dark' },
          scrollbarWidth: 'thin',
          scrollbarColor: `${theme.palette.divider} ${theme.palette.action.hover}`,
        }}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={lists.map((l: KanbanListType) => l.id)}
            strategy={horizontalListSortingStrategy}
          >
            <Box display="flex" gap={isMobile ? 1.5 : 2} sx={{ minWidth: 'max-content' }}>
              {lists.map((list: KanbanListType, index: number) => {
                const visibleCards = getVisibleCards(list.cards || [], list.id);
                const totalCards = (list.cards || []).length;
                const hasMoreCards = totalCards > (cardsPerList[list.id] || INITIAL_CARDS_PER_LIST);
                const prevList = index > 0 ? lists[index - 1] : null;
                const nextList = index < lists.length - 1 ? lists[index + 1] : null;
                return (
                  <SortableList
                    key={list.id}
                    list={list}
                    cards={visibleCards}
                    totalCards={totalCards}
                    hasMoreCards={hasMoreCards}
                    isLoadingMore={isLoadingMore[list.id] || false}
                    onLoadMore={() => loadMoreCards(list.id)}
                    visibleCount={visibleCards.length}
                    prevList={prevList}
                    nextList={nextList}
                    onMoveCard={(cardId: string, targetListId: string) => {
                      const targetList = lists.find((l: KanbanListType) => l.id === targetListId);
                      if (targetList) {
                        moveCardMutation.mutate({
                          card_id: cardId,
                          source_list_id: list.id,
                          destination_list_id: targetListId,
                          destination_position: (targetList.cards || []).length,
                        });
                      }
                    }}
                    onMoveCardInList={(cardId: string, targetPosition: number) => {
                      // Mover card dentro da mesma lista
                      moveCardMutation.mutate({
                        card_id: cardId,
                        source_list_id: list.id,
                        destination_list_id: list.id,
                        destination_position: targetPosition,
                      });
                    }}
                  />
                );
              })}
              <Box
                sx={{
                  minWidth: isMobile ? 260 : 272,
                  maxWidth: isMobile ? 260 : 272,
                  background: theme.palette.background.paper,
                  border: `2px dashed ${theme.palette.divider}`,
                  borderRadius: 2,
                  p: isMobile ? 1.5 : 2,
                  cursor: 'pointer',
                  flexShrink: 0,
                  '&:hover': { background: theme.palette.action.hover },
                }}
                onClick={() => setOpenListDialog(true)}
              >
                <Button
                  fullWidth
                  startIcon={<AddIcon />}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{ color: theme.palette.text.secondary, justifyContent: 'flex-start' }}
                >
                  Adicionar lista
                </Button>
              </Box>
            </Box>
          </SortableContext>
          <DragOverlay>
            {activeCard ? (
              <Box
                sx={{
                  width: isMobile ? 260 : 272,
                  bgcolor: 'background.paper',
                  color: 'text.primary',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  p: 2,
                  boxShadow: theme.shadows[8],
                  transform: 'rotate(3deg)',
                }}
              >
                <Typography variant="body2" sx={{ color: 'text.primary' }}>{activeCard.title}</Typography>
              </Box>
            ) : null}
          </DragOverlay>
        </DndContext>
        </Box>
      </Box>

      {/* Dialog para criar lista */}
      <Dialog open={openListDialog} onClose={() => setOpenListDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ color: 'text.primary' }}>Criar Nova Lista</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome da Lista"
            fullWidth
            variant="outlined"
            value={newListName}
            onChange={(e) => setNewListName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleCreateList();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenListDialog(false)}>Cancelar</Button>
          <Button
            onClick={handleCreateList}
            variant="contained"
            disabled={!newListName.trim() || createListMutation.isPending}
          >
            {createListMutation.isPending ? 'Criando...' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BoardView;

